import { useNavigate, useParams } from 'react-router-dom'
import { Row, Col, Tab, Tabs, Button } from 'react-bootstrap'
import { DcsPermissions, IPageProps, IParamsId, IRootState } from 'types'
import TwilioComplianceEmbeddable from 'components/Integrations/SMS/TwilioComplianceEmbeddable'
import { hasPermission } from 'state/reduxHelpers'
import { ConfirmModal, FormInput, FormSwitch, FormTextArea, MessageAlert, PageHeader, ReturnButton, SaveButton, Spinner } from 'components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FormElement, Field, Form, FormClassComponent } from '@progress/kendo-react-form'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'state/store'
import { V1ApiIntegrationsClient } from 'core/http-common'
import toast from 'core/toast'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { getErrors } from 'core/helpers'
import { ITwilioAccountDto, UpdateTwilioAccountDto } from 'generated/v1/v1IntegrationsApi'
import useQueryParam from 'core/hooks/useQueryParam'

interface IQueryParams {
    code?: string
    message?: string
    tab?: string
}

const SMSEditPage = ({ title }: IPageProps) => {

    const redirectUrl = '/integrations/sms'
    const { id } = useParams<IParamsId>()
    const { code, message, tab } = useQueryParam<IQueryParams>()

    const formRef = useRef<FormClassComponent>(null)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    const loading = useSelector((state: IRootState) => state.app.loading)
    const [account, setAccount] = useState<ITwilioAccountDto | null>(null)
    const [loaded, setLoaded] = useState(false)
    const [activeTab, setActiveTab] = useState(tab || 'ourSettings')
    const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false)
    const [closeModalLoading, setCloseModalLoading] = useState(false)
    const [closeModalErrors, setCloseModalErrors] = useState<string[]>([])
    const [showResendConfirmModal, setShowResendConfirmModal] = useState(false)
    const [resendModalLoading, setResendModalLoading] = useState(false)
    const [resendModalErrors, setResendModalErrors] = useState<string[]>([])
    const [alertDismissed, setAlertDismissed] = useState(false)
    const canEditWelcomeMessage = hasPermission(DcsPermissions.EditSmsWelcomeMessage)

    const loadAccount = useCallback(async () => {
        if (!id) return

        try {
            await dispatch(startLoadingAction())
            const result = await V1ApiIntegrationsClient.getTwilioAccount(id)
            setAccount(result)
            setLoaded(true)
        } catch (e) {
            toast.responseErrors(e)
            navigate(redirectUrl)
        } finally {
            await dispatch(endLoadingAction())
        }
    }, [id, dispatch, navigate])

    useEffect(() => {
        loadAccount()
    }, [loadAccount])

    useEffect(() => {
        if (tab) setActiveTab(tab)
    }, [tab])

    const handleTabSelect = (key: string | null) => {
        if (key) setActiveTab(key)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSubmit = async (values: any) => {
        if (!id)
            return

        try {
            await dispatch(startLoadingAction())
            const dto = new UpdateTwilioAccountDto({
                sendWelcomeMessage: values.sendWelcomeMessage ?? false,
                welcomeMessage: values.welcomeMessage,
                expirationHours: values.messageExpiration ? parseInt(values.messageExpiration) : undefined,
                idleMinutes: values.idleTime ? parseInt(values.idleTime) : undefined,
                hostedSms: values.hostedSms,
                pending: values.pending ?? false
            })

            await V1ApiIntegrationsClient.updateTwilioAccount(id, dto)
            toast.success('SMS Account updated successfully')
        } catch (e) {
            toast.responseErrors(e)
        } finally {
            await dispatch(endLoadingAction())
        }
    }

    const handleResendVerificationClick = () => {
        setResendModalErrors([])
        setShowResendConfirmModal(true)
    }

    const handleResendVerificationConfirmed = async () => {
        if (!id) return

        try {
            setResendModalLoading(true)
            setResendModalErrors([])
            const result = await V1ApiIntegrationsClient.resendTwilioVerification(id)
            setShowResendConfirmModal(false)

            // Update URL with verification code and message to display in alert
            if (result.payload?.verificationCode) {
                const message = encodeURIComponent(`Verification call has been resent to the dealer. Give them this verification code: ${result.payload.verificationCode}`)
                navigate(`/integrations/sms/edit/${id}?code=${result.payload.verificationCode}&message=${message}`, { replace: true })
            }
        } catch (e) {
            setResendModalErrors(getErrors(e))
        } finally {
            setResendModalLoading(false)
        }
    }

    const handleCloseAccountClick = () => {
        setCloseModalErrors([])
        setShowCloseConfirmModal(true)
    }

    const handleCloseAccountConfirmed = async () => {
        if (!id) return

        try {
            setCloseModalLoading(true)
            setCloseModalErrors([])
            await V1ApiIntegrationsClient.closeTwilioAccount(id)
            toast.success('SMS Account closed successfully')
            setShowCloseConfirmModal(false)
            navigate(redirectUrl)
        } catch (e) {
            setCloseModalErrors(getErrors(e))
        } finally {
            setCloseModalLoading(false)
        }
    }

    if (!loaded) {
        return (
            <div id='SMSEditPage'>
                <PageHeader title={title}>
                    <ReturnButton redirectUrl='/integrations/sms' />
                </PageHeader>
                <Spinner center />
            </div>
        )
    }

    return (
        <div id='SMSEditPage'>
            <PageHeader title={title}>
                {!account?.deleted && <Button
                    variant='danger'
                    className='me-2 btn-lg'
                    onClick={handleCloseAccountClick}
                    disabled={loading}
                >
                    Close Account
                </Button>}
                {account?.hostedSms && <Button
                    variant='default'
                    className='me-2 btn-lg'
                    onClick={handleResendVerificationClick}
                    disabled={loading}
                >
                    Resend Verification Call
                </Button>
                }
                <ReturnButton redirectUrl='/integrations/sms' />
            </PageHeader>

            <Form
                onSubmit={handleSubmit}
                ref={formRef}
                ignoreModified={true}
                initialValues={{
                    dealerId: account?.dealerId,
                    phoneNumber: account?.phoneNumber,
                    messageExpiration: account?.expirationHours,
                    idleTime: account?.idleMinutes,
                    welcomeMessage: account?.welcomeMessage,
                    sendWelcomeMessage: account?.sendWelcomeMessage,
                    hostedSms: account?.hostedSms,
                    pending: account?.pending
                }}
                render={(formRenderProps) => (
                    <FormElement>
                        <div className='bg-light border p-3'>
                            {
                                (code || message) && !alertDismissed &&
                                <MessageAlert
                                    dismissible={true}
                                    onClose={() => setAlertDismissed(true)}
                                    alert={{
                                        variant: 'success',
                                        messages: [
                                            message || (code ? `Verification code: ${code}` : '')
                                        ]
                                    }}
                                />
                            }
                            <Tabs activeKey={activeTab} onSelect={handleTabSelect} id='sms-settings-tabs'>
                                <Tab eventKey='ourSettings' title='Our Settings'>
                                    <div className='bg-white border p-3'>
                                        <Row>
                                            <Col lg={6} md={6}>
                                                <Field
                                                    name='dealerId'
                                                    label='Dealer ID:'
                                                    component={FormInput}
                                                    disabled
                                                />
                                            </Col>
                                            <Col lg={6} md={6}>
                                                <Field
                                                    name='phoneNumber'
                                                    label='Phone Number:'
                                                    component={FormInput}
                                                    disabled
                                                />
                                            </Col>
                                            <Col lg={6} md={6}>
                                                <Field
                                                    name='messageExpiration'
                                                    label='Message Expiration(Hours):'
                                                    component={FormInput}
                                                    className='form-control'
                                                    min={0}
                                                    format='n0'
                                                    type='number'
                                                />
                                            </Col>
                                            <Col lg={6} md={6}>
                                                <Field
                                                    name='idleTime'
                                                    label='Idle Time(Minutes):'
                                                    component={FormInput}
                                                    className='form-control'
                                                    min={0}
                                                    format='n0'
                                                    type='number'
                                                />
                                            </Col>
                                            <Col lg={12}>
                                                <Field
                                                    name='welcomeMessage'
                                                    label='Welcome Message:'
                                                    component={FormTextArea}
                                                    className='form-control'
                                                    rows={4}
                                                    disabled={!canEditWelcomeMessage}
                                                />
                                            </Col>
                                            <Col lg={4}>
                                                <Field
                                                    name='sendWelcomeMessage'
                                                    label='Send Welcome Message?'
                                                    component={FormSwitch}
                                                    onLabel='Yes'
                                                    offLabel='No'
                                                />
                                            </Col>
                                            <Col lg={4}>
                                                <Field
                                                    name='hostedSms'
                                                    label='Hosted SMS?'
                                                    component={FormSwitch}
                                                    onLabel='Yes'
                                                    offLabel='No'
                                                    disabled
                                                />
                                            </Col>
                                            <Col lg={4}>
                                                <Field
                                                    name='pending'
                                                    label='Pending?'
                                                    component={FormSwitch}
                                                    onLabel='Yes'
                                                    offLabel='No'
                                                />
                                            </Col>
                                        </Row>
                                        <SaveButton
                                            type='submit'
                                            className='btn-lg btn-primary btnWidth mt-3'
                                            disabled={!formRenderProps.allowSubmit || loading}
                                        >
                                            Save Changes
                                        </SaveButton>
                                    </div>
                                </Tab>
                                <Tab eventKey='regulatorySettings' title='Regulatory Settings'>
                                    <div className='bg-white border p-3'>
                                        {account?.hostedSms === false && account && (
                                            <TwilioComplianceEmbeddable
                                                account={account}
                                                onStatusChange={loadAccount}
                                            />
                                        )}
                                        {account?.hostedSms === true && (
                                            <MessageAlert
                                                alert={{
                                                    variant: 'info',
                                                    messages: ['Regulatory compliance is managed through Twilio\'s hosted number verification process.']
                                                }}
                                            />
                                        )}
                                    </div>
                                </Tab>
                            </Tabs>
                        </div>
                    </FormElement>
                )}
            />

            <ConfirmModal
                show={showCloseConfirmModal}
                loading={closeModalLoading}
                disabled={closeModalLoading}
                errors={closeModalErrors}
                title='Close SMS Account'
                confirmButtonTitle='Close Account'
                confirmButtonVariant='danger'
                content='Are you sure you want to close this SMS account? This action cannot be undone.'
                onCancel={() => setShowCloseConfirmModal(false)}
                onSave={handleCloseAccountConfirmed}
            />

            <ConfirmModal
                show={showResendConfirmModal}
                loading={resendModalLoading}
                disabled={resendModalLoading}
                errors={resendModalErrors}
                title='Resend Verification Call'
                confirmButtonTitle='Resend'
                confirmButtonVariant='primary'
                content='Are you sure you want to resend the verification call to the dealer?'
                onCancel={() => setShowResendConfirmModal(false)}
                onSave={handleResendVerificationConfirmed}
            />
        </div>
    )
}
export default SMSEditPage
